import React from 'react';
import { Lock, AlertTriangle, ShieldCheck, Info } from 'lucide-react';

const TRIPLET = [
  ['ilpPacket', 'base64url, OER-encoded', 'Carries the transfer details and the condition. Generated by the payee side (Mojaloop SDK) — you forward it verbatim, never parse or regenerate it.'],
  ['condition', 'base64url SHA-256 digest', 'The 32-byte hash the fulfilment must satisfy. Travels with the quote and the transfer.'],
  ['fulfilment', 'base64url 32-byte preimage', 'Returned by the payee. Valid only if SHA-256(fulfilment) equals the condition.'],
];

const STAGES = [
  ['Quote', 'The quote response carries the ilpPacket and condition (minted by the payee/SDK).', '#2563eb'],
  ['Transfer', 'Pass the ilpPacket and condition through verbatim in the transfer request.', '#2563eb'],
  ['Fulfilment', 'The payee credits the payee account and returns the fulfilment preimage.', '#16a34a'],
  ['Verify & commit', 'Check SHA-256(fulfilment) == condition. Only then is the transfer settled.', '#16a34a'],
];

const PITFALLS = [
  ['Regenerating the packet', 'The ilpPacket and condition are shuttled, not generated. Forward exactly what the quote returned — re-encoding or rebuilding the packet breaks the cryptographic binding.'],
  ['Hashing the base64 string', 'The condition is SHA-256 over the decoded fulfilment bytes, not over its base64url text. Decode first, then hash.'],
  ['Standard base64', 'condition and fulfilment are base64url (URL-safe, no padding). Standard base64 will not match.'],
  ['Committing before verifying', 'Never credit or commit until SHA-256(fulfilment) == condition holds. A mismatch means the fulfilment is invalid — reject the transfer.'],
  ['Ignoring expiry', 'The transfer carries an expiry. A fulfilment that arrives after expiry must be rejected even if the hash matches.'],
];

export default function IlpGuide() {
  return (
    <div className="space-y-8 max-w-4xl">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Lock className="w-6 h-6 text-primary-600" />
          ILP Condition &amp; Fulfilment
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Every FSPIOP transfer is cryptographically bound by an Interledger condition/fulfilment
          pair. Getting this right is what makes a transfer atomic — the payer only commits once the
          payee proves it credited the funds.
        </p>
      </div>

      {/* Concept callout */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3">
        <Info className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
        <p className="text-sm text-blue-900">
          The binding is one equation: <span className="font-mono">SHA-256(fulfilment) == condition</span>.
          The condition is published up front; only the party that actually performs the transfer can
          produce a fulfilment that hashes to it.
        </p>
      </div>

      {/* The triplet */}
      <section>
        <h2 className="text-lg font-semibold text-gray-900 mb-3">The triplet</h2>
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <tbody>
              {TRIPLET.map(([k, fmt, desc], i) => (
                <tr key={k} className={i % 2 ? 'bg-gray-50' : ''}>
                  <td className="px-4 py-2.5 font-mono text-xs text-primary-700 align-top whitespace-nowrap">{k}</td>
                  <td className="px-4 py-2.5 align-top">
                    <div className="text-xs text-gray-400">{fmt}</div>
                    <div className="text-sm text-gray-700 mt-0.5">{desc}</div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Lifecycle diagram */}
      <section>
        <h2 className="text-lg font-semibold text-gray-900 mb-3">Lifecycle</h2>
        <div className="bg-gray-50 border border-gray-100 rounded-xl p-4">
          <svg viewBox="0 0 680 150" width="100%" role="img"
               aria-label="ILP lifecycle: quote carries condition, transfer forwards it, payee returns fulfilment, payer verifies hash and commits">
            {STAGES.map(([name], i) => {
              const x = 24 + i * 162;
              const color = STAGES[i][2];
              return (
                <g key={name}>
                  <rect x={x} y="40" width="140" height="44" rx="8" fill="#fff" stroke={color} strokeWidth="1.2" />
                  <text x={x + 70} y="67" textAnchor="middle" fontSize="13" fontFamily="system-ui" fill="#0f172a">{name}</text>
                  {i < STAGES.length - 1 && (
                    <g>
                      <line x1={x + 140} y1="62" x2={x + 156} y2="62" stroke="#94a3b8" strokeWidth="1.5" />
                      <path d={`M${x + 162},62 L${x + 154},58 L${x + 154},66 Z`} fill="#94a3b8" />
                    </g>
                  )}
                </g>
              );
            })}
            {/* condition thread */}
            <text x="24" y="24" fontSize="11" fontFamily="ui-monospace, monospace" fill="#64748b">condition travels with the quote and transfer →</text>
            {/* verify callout under last box */}
            <text x="592" y="108" textAnchor="middle" fontSize="11" fontFamily="ui-monospace, monospace" fill="#16a34a">SHA-256(fulfilment) == condition</text>
          </svg>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3">
          {STAGES.map(([name, desc]) => (
            <div key={name} className="bg-white border border-gray-200 rounded-lg p-3">
              <p className="text-sm font-medium text-gray-900">{name}</p>
              <p className="text-xs text-gray-600 mt-0.5">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* The one check you implement */}
      <section>
        <h2 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-green-600" /> The one check you implement
        </h2>
        <p className="text-sm text-gray-600 mb-3">
          The connector shuttles the packet and condition unchanged. The single computation on your
          side is verifying the returned fulfilment before crediting:
        </p>
        <pre className="bg-gray-900 text-gray-100 text-xs rounded-lg p-4 overflow-x-auto leading-relaxed">{`// Verify the payee's fulfilment satisfies the condition (base64url throughout)
byte[] fulfilmentBytes = Base64.getUrlDecoder().decode(fulfilment);
byte[] digest = MessageDigest.getInstance("SHA-256").digest(fulfilmentBytes);
String computed = Base64.getUrlEncoder().withoutPadding().encodeToString(digest);

if (!computed.equals(condition)) {
    throw new IllegalStateException("Invalid fulfilment — does not satisfy condition");
}
// only now: commit / credit the transfer`}</pre>
      </section>

      {/* Pitfalls */}
      <section>
        <h2 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-amber-500" /> Common pitfalls
        </h2>
        <div className="space-y-2">
          {PITFALLS.map(([title, desc]) => (
            <div key={title} className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-sm font-medium text-amber-900">{title}</p>
              <p className="text-sm text-amber-800 mt-0.5">{desc}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
